import { Maximize2 } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { DialogDto } from '../../api/types';
import { useMessages } from '../../hooks/use-messages';
import { Skeleton } from '../ui/skeleton';
import { ChannelBadge } from './ChannelBadge';
import { Composer } from './Composer';
import { DialogRowMenu } from './DialogRowMenu';
import { HandoffActions } from './HandoffActions';
import { MessageBubble } from './MessageBubble';
import { SlaBadge } from './SlaBadge';
import { StateBadge } from './StateBadge';

export interface DialogConversationProps {
  dialog: DialogDto;
  /** Rendered at the left of the header (e.g. a "← Инбокс" back link on the standalone page). */
  headerLeft?: ReactNode;
  /** Rendered at the right of the header, before the state/SLA badges (e.g. the 1024–1280px "Карточка" toggle). */
  headerRight?: ReactNode;
  /** Shows a "открыть в отдельной странице" link to /dialogs/:id — used from the inbox/kanban panels. */
  showOpenFullLink?: boolean;
}

/**
 * The conversation half of a dialog: header, handoff actions, message feed and composer.
 * Shared by the standalone /dialogs/:id page, the two-pane inbox and the kanban side panel.
 */
export function DialogConversation({ dialog, headerLeft, headerRight, showOpenFullLink = false }: DialogConversationProps) {
  const { messages, isLoading: messagesLoading } = useMessages(dialog.id);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  return (
    // `data-testid` is the one stable handle e2e has on "the open conversation":
    // in the two-pane inbox the list on the left renders the same state/SLA
    // badges, so an unscoped text query would be ambiguous.
    <div className="flex min-w-0 flex-1 flex-col" data-testid="dialog-conversation">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {headerLeft}
          <ChannelBadge type={dialog.channelType} showLabel />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {headerRight}
          <StateBadge state={dialog.state} />
          <SlaBadge sla={dialog.slaStatus} />
          <DialogRowMenu dialog={dialog} />
          {showOpenFullLink && (
            <Link
              to={`/dialogs/${dialog.id}`}
              aria-label="Открыть в отдельной странице"
              title="Открыть в отдельной странице"
              className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
            >
              <Maximize2 className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
      <HandoffActions dialog={dialog} />
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
        {messagesLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="ml-auto h-12 w-2/3" />
          </div>
        )}
        {!messagesLoading && messages.length === 0 && (
          <p className="text-center text-sm text-muted-fg">Сообщений пока нет.</p>
        )}
        <div className="flex flex-col gap-2">
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
        </div>
        <div ref={bottomRef} />
      </div>
      <Composer dialog={dialog} />
    </div>
  );
}
